<template>
  <div>
    <modal v-if="showModal" v-on:close="showModal = false">
      <div slot="header">Delete Location</div>
      <div slot="body">
        <p>
          Are you sure you want to delete the location -
          <strong>{{ deleteLocationLabel }}?</strong>
        </p>
        <p>This will remove it from this report and all future reports for this company.</p>
      </div>
      <div slot="footer">
        <button
          type="button"
          class="btn btn-default"
          data-dismiss="modal"
          v-on:click="closeDeleteModal()"
        >
          Cancel
        </button>
        <button type="button" class="btn btn-danger" v-on:click="deleteLocation()">
          Delete location
        </button>
      </div>
    </modal>
    <div>
      <div class="panel panel-default summaryPanel">
        <div class="panel-heading">
          <div class="row">
            <div class="col-xs-10">
              <h5>{{ location.label }}</h5>
              <div>{{ displayAddress }}</div>
            </div>
            <div v-if="isLocationInGb(location)" class="col-xs-2" style="text-align: right">
              <a class="accordion-toggle" data-toggle="collapse" :href="locationAnchor"></a>
            </div>
          </div>
        </div>
        <div v-if="isLocationInGb(location)" :id="location.id" class="panel-collapse collapse in">
          <div class="panel-body">
            <location-map :report="report" :location="location"></location-map>
            <div class="row">
              <div class="col-xs-12">
                <div id="sectionSpace"></div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <h5>Underwriting Guide</h5>
                <table v-if="location.risk" class="table table-striped table-condensed">
                  <tbody>
                    <tr v-if="location.risk.theft_rag">
                      <th scope="row">Theft:</th>
                      <td v-bind:class="lowercaseClasses(location.risk.theft_rag) + 'RAG'">
                        <p>
                          <strong>{{ location.risk.theft_rag }}:</strong>
                          {{ getUnderwritingComment('theft', location.risk.theft_rag) }}
                        </p>
                      </td>
                    </tr>
                    <tr v-if="location.risk.subsidence_rag">
                      <th scope="row">Subsidence:</th>
                      <td v-bind:class="lowercaseClasses(location.risk.subsidence_rag) + 'RAG'">
                        <p>
                          <strong>{{ location.risk.subsidence_rag }}:</strong>
                          {{ getUnderwritingComment('subsidence', location.risk.subsidence_rag) }}
                        </p>
                      </td>
                    </tr>
                    <tr v-if="location.risk.flood_rag">
                      <th scope="row">Flood:</th>
                      <td v-bind:class="lowercaseClasses(location.risk.flood_rag) + 'RAG'">
                        <p>
                          <strong>{{ location.risk.flood_rag }}:</strong>
                          {{ getUnderwritingComment('flood', location.risk.flood_rag) }}
                        </p>
                      </td>
                    </tr>
                    <tr v-if="location.risk.terrorism_rating">
                      <th scope="row">Terrorism:</th>
                      <td class="neutralRAG">
                        <p>
                          <strong>Pool Re zone: {{ location.risk.terrorism_rating }}</strong>
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="no-data">No data available for this location</div>
              </div>
              <div class="col-md-6">
                <h5>Fire Response Times</h5>
                <table v-if="location.risk" class="table table-striped table-condensed">
                  <thead>
                    <tr>
                      <th scope="row"></th>
                      <th scope="row">High-volume pumping unit:</th>
                      <th scope="row">Aerial ladder platform</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">9 - 5:</th>
                      <td v-bind:class="lowercaseClasses(location.risk.fire_9to5_hvp_rag) + 'RAG'">
                        <p>
                          <strong>
                            {{ isEmptyMessage(location.risk.fire_9to5_hvp_rag) }}
                          </strong>
                          {{
                            isEmptyMessage(SecondsTohhmmss(location.risk.fire_9to5_hvp_response))
                          }}
                        </p>
                      </td>

                      <td
                        v-bind:class="lowercaseClasses(location.risk.fire_9to5_aerial_rag) + 'RAG'"
                      >
                        <p>
                          <strong>
                            {{ isEmptyMessage(location.risk.fire_9to5_aerial_rag) }}
                          </strong>
                          {{
                            isEmptyMessage(SecondsTohhmmss(location.risk.fire_9to5_aerial_response))
                          }}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">8 - 6:</th>
                      <td v-bind:class="lowercaseClasses(location.risk.fire_8to6_hvp_rag) + 'RAG'">
                        <p>
                          <strong>
                            {{ isEmptyMessage(location.risk.fire_8to6_hvp_rag) }}
                          </strong>
                          {{
                            isEmptyMessage(SecondsTohhmmss(location.risk.fire_8to6_hvp_response))
                          }}
                        </p>
                      </td>

                      <!-- TODO: Fix this variable name -->
                      <td
                        v-bind:class="lowercaseClasses(location.risk.fire_9to6_aerial_rag) + 'RAG'"
                      >
                        <p>
                          <strong>
                            {{ isEmptyMessage(location.risk.fire_9to6_aerial_rag) }}
                          </strong>
                          {{
                            isEmptyMessage(SecondsTohhmmss(location.risk.fire_8to6_aerial_response))
                          }}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="no-data">No data available for this location</div>
                <div class="row">
                  <div class="col-xs-12">
                    <div id="sectionSpace"></div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row" v-show="location.type_id !== 1">
              <div class="col-xs-12 text-right">
                <button
                  type="button"
                  class="btn btn-danger"
                  v-on:click="openDeleteModal(location.id, location.label)"
                >
                  Delete location
                </button>
              </div>
            </div>
            <div class="row" v-show="location.type_id === 1">
              <div class="col-xs-12 text-right">
                <button type="button" class="btn btn-default" disabled>
                  Registered offices cannot be deleted
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Modal from '@/components/app/Modal';
import LocationMap from '@/components/reports/panels/locations/Map';
import toolsMixin from '@/components/reports/ToolsMixin';
import Axios from '@/packages/axios/Axios';

export default {
  components: {
    LocationMap,
    Modal,
  },
  mixins: [toolsMixin],
  props: ['report', 'location', 'locationLabel'],
  data() {
    return {
      showModal: false,
      deleteLocationId: 0,
      deleteLocationLabel: '',
    };
  },
  computed: {
    locationAnchor() {
      return '#' + this.location.id;
    },
    displayAddress() {
      if (this.location.full !== '') {
        return this.isEmptyMessage(this.location.full);
      }
      return this.isEmptyMessage(this.location.postcode);
    },
  },
  methods: {
    isLocationInGb(location) {
      /*
       * We currently allow only locations in GB, and so if the country code doesn't exist, we
       * assume that the location is in the GB as otherwise the location could not have been added.
       */
      if (
        location.country === undefined ||
        location.country === null ||
        location.country.code === 'gb'
      ) {
        return true;
      }
      return false;
    },
    openDeleteModal(locationId, locationLabel) {
      this.deleteLocationId = locationId;
      this.deleteLocationLabel = locationLabel;
      this.showModal = true;

      return true;
    },
    deleteLocation() {
      this.showModal = false;
      Axios.delete('/v1/customlocations/' + this.report.id + '/' + this.deleteLocationId, {})
        .then((response) => {
          this.$emit('on-change', response.data[0]);
        })
        .catch((e) => {
          if (e.response) {
            this.errors.push(e);
            switch (e.response.status) {
              case 401:
                this.$router.push('/error/401');
                break;
              case 403:
                this.$router.push('/error/403');
                break;
              case 404:
                this.$router.push('/error/404');
                break;
              default:
                this.$router.push('/error/unknown');
            }
          }
        });
    },
    lowercaseClasses(value) {
      if (value != null) {
        return value.toLowerCase();
      }
      return '';
    },
    closeDeleteModal() {
      this.showModal = false;
    },
  },
};
</script>
